import { alpha } from '@mui/material/styles';

import { mediaQuery } from 'constants/ui';

const SEGMENT_SIZE = 30;
const SEGMENT_SIZE_MOBILE = 28;
const PADDING = 4;

// White-overlay opacities tuned for the brand-colored (light) and dark-surface headers.
const TRACK_ALPHA_DARK = 0.06;
const TRACK_ALPHA_LIGHT = 0.18;
// On a neutral menu surface (light) the white-on-brand scheme is invisible, so the track
// is a faint text tint (iOS-style: gray track + white selected pill).
const TRACK_ALPHA_SURFACE_LIGHT = 0.08;
const INDICATOR_ALPHA_DARK = 0.16;
const INACTIVE_ICON_ALPHA_LIGHT = 0.72;

const isDarkMode = palette => palette.mode === 'dark';

// Keep the short labels ("Light mode" / "Dark mode") on a single row.
export const tooltipSlotProps = { tooltip: { sx: { whiteSpace: 'nowrap', maxWidth: 'none' } } };

const getTrackColor = (palette, onSurface) => {
  if (isDarkMode(palette)) {
    return alpha(palette.common.white, TRACK_ALPHA_DARK);
  }

  return onSurface
    ? alpha(palette.text.primary, TRACK_ALPHA_SURFACE_LIGHT)
    : alpha(palette.common.white, TRACK_ALPHA_LIGHT);
};

export const getContainerStyles =
  (onSurface = false) =>
  ({ palette }) => ({
    position: 'relative',
    display: 'inline-flex',
    alignItems: 'center',
    flexShrink: 0,
    padding: `${PADDING}px`,
    borderRadius: '999px',
    backgroundColor: getTrackColor(palette, onSurface),
    border: isDarkMode(palette) ? `1px solid ${palette.border.light}` : 'none',
  });

export const getIndicatorStyles =
  isDark =>
  ({ palette }) => ({
    position: 'absolute',
    top: `${PADDING}px`,
    left: `${PADDING}px`,
    width: `calc(50% - ${PADDING}px)`,
    height: `calc(100% - ${PADDING * 2}px)`,
    borderRadius: '999px',
    backgroundColor: isDarkMode(palette)
      ? alpha(palette.common.white, INDICATOR_ALPHA_DARK)
      : palette.common.white,
    boxShadow: isDarkMode(palette) ? 'none' : palette.shadow.s,
    transform: isDark ? 'translateX(100%)' : 'translateX(0)',
    transition: 'transform 0.22s cubic-bezier(0.4, 0, 0.2, 1)',
  });

export const getSegmentStyles =
  (isActive, onSurface = false) =>
  ({ palette }) => {
    const dark = isDarkMode(palette);
    const onNeutral = dark || onSurface;

    const activeColor = dark ? palette.primary.light : palette.primary.main;
    const inactiveColor = onNeutral
      ? palette.text.secondary
      : alpha(palette.common.white, INACTIVE_ICON_ALPHA_LIGHT);
    const inactiveHoverColor = onNeutral ? palette.text.primary : palette.common.white;

    return {
      position: 'relative',
      zIndex: 1,
      width: `${SEGMENT_SIZE}px`,
      height: `${SEGMENT_SIZE}px`,
      padding: 0,
      borderRadius: '999px',
      color: isActive ? activeColor : inactiveColor,
      transition: 'color 0.2s ease',

      '&:hover': {
        backgroundColor: 'transparent',
        color: isActive ? activeColor : inactiveHoverColor,
      },

      [mediaQuery.mobileAndTablet]: {
        width: `${SEGMENT_SIZE_MOBILE}px`,
        height: `${SEGMENT_SIZE_MOBILE}px`,
      },
    };
  };
